<template>
	<div ref="gridContainerRef" class="ag-grid-wrapper" :class="{ 'ag-grid-wrapper--auto-height': autoHeight }">
		<ag-grid-vue
			ref="agGridRef"
			:class="['ag-theme-material', 'ag-grid-wrapper__grid', { 'ag-grid-wrapper__grid--auto-height': autoHeight }]"
			:theme="'legacy'"
			:localeText="localeText"
			:rowData="rowData"
			:pinnedBottomRowData="pinnedBottomRowData"
			:getRowId="getRowId"
			:columnDefs="normalizedColDefs"
			:context="context"
			:defaultColDef="defaultColDef"
			:rowHeight="rowHeight"
			:domLayout="autoHeight ? 'autoHeight' : undefined"
			:autoGroupColumnDef="resolvedAutoGroupColumnDef"
			:groupDisplayType="groupDisplayType"
			:groupDefaultExpanded="groupDefaultExpanded"
			:getGroupRowAgg="getGroupRowAgg"
			:getRowStyle="getRowStyle"
			:getRowClass="getRowClass"
			:rowDragManaged="true"
			:animateRows="true"
			:columnHoverHighlight="true"
			:columnMenu="'new'"
			:rowGroupPanelShow="rowGroupPanelShow"
			:sideBar="sideBarDef"
			:suppressAggFuncInHeader="true"
			:suppressDragLeaveHidesColumns="true"
			:suppressNoRowsOverlay="true"
			:pagination="pagination"
			:paginationPageSize="paginationPageSize"
			:enableCharts="enableCharts"
			:cellSelection="cellSelection"
			@grid-ready="onGridReady"
			@first-data-rendered="onModelUpdated"
			@row-data-updated="onModelUpdated"
			@model-updated="onModelUpdated"
			@column-row-group-changed="onRowGroupChanged"
			@sort-changed="onViewStateChanged"
			@filter-changed="onViewStateChanged"
			@column-moved="onViewStateChanged"
			@column-visible="onViewStateChanged"
			@column-pinned="onViewStateChanged"
			@column-resized="onColumnResized"
			@displayed-columns-changed="refreshGroupDividers"
			@drag-started="onHeaderDragStarted"
			@drag-stopped="onHeaderDragStopped"
			@grid-size-changed="syncGridLayoutWithColumnFit"
			@row-drag-end="rowsChanged"
			@cell-value-changed="rowsChanged"
			@cell-key-down="onCellKeyDown"
		></ag-grid-vue>
	</div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { AgGridVue } from 'ag-grid-vue3';
import { getAgGridLocaleText, getCurrentAppLocale } from '@/utils/agGrid/agGridLocale';
import { withGroupDividers } from '@/utils/agGrid/agGridGroupDivider';
import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-material.css';

const agGridRef = ref(null);
const gridContainerRef = ref(null);
let resizeObserver = null;
let autoSizeFrameId = null;
let pendingViewState = null;
let pendingViewStateOptions = null;
let lastAppliedViewState = null;
let lastAppliedViewStateOptions = null;
let remainingViewStateReplays = 0;
let viewStateReplayFrameId = null;
let isHeaderDragging = false;

const props = defineProps({
	rowData: {
		type: Array,
		default: () => [],
	},
	getRowId: {
		type: Function,
		default: (params) => {
			return params.data.id || '';
		},
	},
	colDefs: {
		type: Array,
		default: () => [],
	},
	getGroupRowAgg: {
		type: Function,
		default: () => {
			return null;
		},
	},
	sideBar: {
		type: [Boolean, Object],
		default: false,
	},
	rowGroupPanelShow: {
		type: String,
		default: 'always',
	},
	pagination: {
		type: Boolean,
		default: false,
	},
	paginationPageSize: {
		type: Number,
		default: 100,
	},
	enableCharts: {
		type: Boolean,
		default: false,
	},
	cellSelection: {
		type: [Boolean, Object],
		default: false,
	},
	pinnedBottomRowData: {
		type: Array,
		default: null,
	},
	autoSizeColumns: {
		type: Boolean,
		default: true,
	},
	rowHeight: {
		type: Number,
		default: undefined,
	},
	autoHeight: {
		type: Boolean,
		default: false,
	},
	context: {
		type: Object,
		default: () => ({}),
	},
	getRowClass: {
		type: Function,
		default: null,
	},
	autoGroupColumnDefOverride: {
		type: Object,
		default: () => ({}),
	},
	autoGroupColumnDef: {
		type: Object,
		default: null,
	},
	groupDefaultExpanded: {
		type: Number,
		default: 0,
	},
	groupDisplayType: {
		type: String,
		default: 'groupRows',
	},
});

const emit = defineEmits(['update:rowData', 'displayed-row-count-changed', 'row-group-columns-changed', 'grid-ready', 'view-state-changed']);
const inferredNumericColumnKeys = ref(new Set());
const AUTO_GROUP_COLUMN_ID = 'ag-Grid-AutoColumn';

const localeText = computed(() => getAgGridLocaleText(getCurrentAppLocale()));
const normalizedColDefs = computed(() => normalizeColDefs(props.colDefs).map((colDef) => withGroupDividers(colDef)));
const hasColumnGroups = computed(() => props.colDefs.some((colDef) => Array.isArray(colDef?.children)));

const defaultColDef = computed(() => ({
	sortable: true,
	filter: true,
	resizable: true,
	minWidth: 120,
	enableRowGroup: true,
	suppressHeaderMenuButton: false,
	suppressSpanHeaderHeight: true,
	mainMenuItems: (params) => getMainMenuItems(params),
}));

const AUTO_GROUP_COLUMN_DEFAULTS = {
	colId: AUTO_GROUP_COLUMN_ID,
	filter: 'agGroupColumnFilter',
	pinned: 'left',
};

const resolvedAutoGroupColumnDef = computed(() => ({
	...AUTO_GROUP_COLUMN_DEFAULTS,
	...(props.autoGroupColumnDefOverride || {}),
	...(props.autoGroupColumnDef || {}),
}));

const sideBarDef = computed(() => {
	if (props.sideBar === true) {
		return {
			toolPanels: [
				{
					id: 'columns',
					labelDefault: 'Columns',
					labelKey: 'columns',
					iconKey: 'columns',
					toolPanel: 'agColumnsToolPanel',
					minWidth: 180,
					maxWidth: 400,
					width: 350,
				},
				{
					id: 'filters',
					labelDefault: 'Filters',
					labelKey: 'filters',
					iconKey: 'filter',
					toolPanel: 'agFiltersToolPanel',
					minWidth: 180,
					maxWidth: 400,
					width: 350,
				},
			],
			position: 'right',
			defaultToolPanel: null,
		};
	}

	if (typeof props.sideBar === 'object' && props.sideBar !== null) {
		return props.sideBar;
	}

	return false;
});

onMounted(() => {
	init();
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', syncGridLayoutWithColumnFit);

	if (autoSizeFrameId !== null) {
		cancelAnimationFrame(autoSizeFrameId);
		autoSizeFrameId = null;
	}

	if (viewStateReplayFrameId !== null) {
		cancelAnimationFrame(viewStateReplayFrameId);
		viewStateReplayFrameId = null;
	}

	if (resizeObserver) {
		resizeObserver.disconnect();
		resizeObserver = null;
	}
});

watch(
	() => props.rowData,
	() => {
		nextTick(() => {
			const api = agGridRef.value?.api;
			api?.refreshCells({ force: true });
			replayLastAppliedViewStateIfNeeded();
			syncGridLayoutWithColumnFit();
			emitDisplayedRowCount();
		});
	},
	{ deep: false }
);

watch(
	() => props.colDefs,
	() => {
		nextTick(() => {
			applyPendingViewStateIfReady();
			replayLastAppliedViewStateIfNeeded();
			syncGridLayoutWithColumnFit();
			emitDisplayedRowCount();
		});
	},
	{ deep: false }
);

watch(
	() => [props.colDefs, props.rowData],
	() => {
		updateInferredNumericColumns();
	},
	{ deep: false, immediate: true }
);

function init() {
	nextTick(() => {
		syncGridLayoutWithColumnFit();
		observeContainer();
	});

	window.addEventListener('resize', syncGridLayoutWithColumnFit);
}

function observeContainer() {
	if (typeof ResizeObserver === 'undefined') {
		return;
	}

	const observedContainer = getObservedContainer() || gridContainerRef.value?.parentElement;
	if (!observedContainer) {
		return;
	}

	resizeObserver = new ResizeObserver(() => {
		syncGridLayoutWithColumnFit();
	});
	resizeObserver.observe(observedContainer);
}

// Returns a height-bounded ancestor (one whose height does NOT grow with the grid).
// Returns null when none exists — callers must then size the grid independently of
// its own height to avoid a ResizeObserver feedback loop.
function getObservedContainer() {
	const gridContainerElement = gridContainerRef.value;
	if (!gridContainerElement) {
		return null;
	}

	return gridContainerElement.closest('.v-card-text') || null;
}

function onGridReady() {
	nextTick(() => {
		syncGridLayoutWithColumnFit();
		emitDisplayedRowCount();
		emitRowGroupColumns();
		emit('grid-ready');
	});
}

function onModelUpdated() {
	replayLastAppliedViewStateIfNeeded();
	syncGridLayoutWithColumnFit();
	emitDisplayedRowCount();
	emitRowGroupColumns();
}

function onRowGroupChanged() {
	emitRowGroupColumns();
	emitViewStateChanged();
}

function onViewStateChanged() {
	emitViewStateChanged();
}

function onColumnResized(event) {
	if (event?.finished) {
		emitViewStateChanged();
	}
}

function emitViewStateChanged() {
	emit('view-state-changed');
}

function refreshGroupDividers() {
	const api = agGridRef.value?.api;
	if (!api || !hasColumnGroups.value) {
		return;
	}

	api.refreshCells();
	if (!isHeaderDragging) {
		api.refreshHeader();
	}
}

function onHeaderDragStarted() {
	isHeaderDragging = true;
}

function onHeaderDragStopped() {
	isHeaderDragging = false;
	refreshGroupDividers();
}

function getMainMenuItems(params) {
	const defaultItems = Array.isArray(params.defaultItems) ? [...params.defaultItems] : [];
	const preferredItems = ['sortAscending', 'sortDescending', 'sortUnSort', 'separator', 'pinSubMenu', 'separator', 'autoSizeThis', 'autoSizeAll', 'separator', 'resetColumns', 'columnFilter'];
	const mergedItems = [];

	preferredItems.forEach((item) => {
		if (!mergedItems.includes(item)) {
			mergedItems.push(item);
		}
	});

	defaultItems.forEach((item) => {
		if (!mergedItems.includes(item)) {
			mergedItems.push(item);
		}
	});

	return mergedItems;
}

function getRowStyle(params) {
	if (params.node && params.node.group) {
		return { background: '#1c79bb35', fontWeight: 'bold' };
	}
	return null;
}

function updateInferredNumericColumns() {
	const nextKeys = collectNumericColumnKeys(props.colDefs, props.rowData);

	if (!areSetsEqual(nextKeys, inferredNumericColumnKeys.value)) {
		inferredNumericColumnKeys.value = nextKeys;
	}
}

function collectNumericColumnKeys(colDefs = [], rowData = [], path = 'root') {
	const numericKeys = new Set();

	colDefs.forEach((colDef, index) => {
		collectNumericColumnKeysForDef(colDef, rowData, numericKeys, `${path}.${index}`);
	});

	return numericKeys;
}

function collectNumericColumnKeysForDef(colDef, rowData, numericKeys, path) {
	if (!colDef || typeof colDef !== 'object') {
		return;
	}

	if (Array.isArray(colDef.children)) {
		colDef.children.forEach((childDef, index) => {
			collectNumericColumnKeysForDef(childDef, rowData, numericKeys, `${path}.${index}`);
		});
		return;
	}

	if (isNumericColDef(colDef, rowData)) {
		numericKeys.add(getColDefKey(colDef, path));
	}
}

function normalizeColDefs(colDefs = [], path = 'root') {
	return colDefs.map((colDef, index) => normalizeColDef(colDef, `${path}.${index}`));
}

function normalizeColDef(colDef, path) {
	if (!colDef || typeof colDef !== 'object') {
		return colDef;
	}

	if (Array.isArray(colDef.children)) {
		return {
			...colDef,
			children: normalizeColDefs(colDef.children, path),
		};
	}

	const defaultAggFunc = getDefaultAggFunc(colDef, path);
	if (!defaultAggFunc) {
		return colDef;
	}

	return {
		...colDef,
		enableValue: colDef.enableValue ?? true,
		aggFunc: defaultAggFunc,
	};
}

function getDefaultAggFunc(colDef, path) {
	if (Object.prototype.hasOwnProperty.call(colDef, 'aggFunc')) {
		return null;
	}

	if (colDef.enableValue === false) {
		return null;
	}

	if (!hasColumnValueSource(colDef)) {
		return null;
	}

	return inferredNumericColumnKeys.value.has(getColDefKey(colDef, path)) ? 'sum' : 'count';
}

function isNumericColDef(colDef, rowData = []) {
	if (colDef.cellDataType === 'number') {
		return true;
	}

	const sampleValue = getSampleColumnValue(colDef, rowData);
	return (typeof sampleValue === 'number' && Number.isFinite(sampleValue)) || typeof sampleValue === 'bigint';
}

function getSampleColumnValue(colDef, rowData = []) {
	if (!Array.isArray(rowData)) {
		return undefined;
	}

	for (const row of rowData) {
		const value = resolveColumnValue(row, colDef);
		if (value !== null && value !== undefined) {
			return value;
		}
	}

	return undefined;
}

function resolveColumnValue(row, colDef) {
	if (!row || typeof row !== 'object') {
		return undefined;
	}

	if (typeof colDef.valueGetter === 'function') {
		try {
			return colDef.valueGetter({
				data: row,
				colDef,
				getValue: (field) => getFieldValue(row, field),
			});
		} catch {
			return undefined;
		}
	}

	if (typeof colDef.field === 'string' && colDef.field.length > 0) {
		return getFieldValue(row, colDef.field);
	}

	return undefined;
}

function hasColumnValueSource(colDef) {
	return (typeof colDef.field === 'string' && colDef.field.length > 0) || typeof colDef.valueGetter === 'function';
}

function getFieldValue(source, field) {
	return field.split('.').reduce((currentValue, segment) => currentValue?.[segment], source);
}

function getColDefKey(colDef, path) {
	return colDef.colId || colDef.field || `${path}:${colDef.headerName || 'column'}`;
}

function areSetsEqual(left, right) {
	if (left.size !== right.size) {
		return false;
	}

	for (const value of left) {
		if (!right.has(value)) {
			return false;
		}
	}

	return true;
}

function syncGridLayoutWithColumnFit() {
	syncGridLayout({ fitColumns: true });
}

function syncGridLayout(options = {}) {
	const api = agGridRef.value?.api;
	const gridElement = gridContainerRef.value;
	const shouldFitColumns = options?.fitColumns === true;

	if (!api || !gridElement) {
		return;
	}

	setGridHeight(gridElement);

	if (shouldFitColumns && props.autoSizeColumns) {
		if (autoSizeFrameId !== null) {
			cancelAnimationFrame(autoSizeFrameId);
		}

		autoSizeFrameId = requestAnimationFrame(() => {
			autoSizeFrameId = null;
			autoSizeColumns(api);
		});
	}
}

function setGridHeight(gridElement) {
	if (props.autoHeight) {
		return;
	}

	const observedContainer = getObservedContainer();
	const minimumHeight = 260;
	let availableHeight = 0;

	if (observedContainer) {
		const computedStyle = window.getComputedStyle(observedContainer);
		const verticalPadding = Number.parseFloat(computedStyle.paddingTop || '0') + Number.parseFloat(computedStyle.paddingBottom || '0');
		availableHeight = observedContainer.clientHeight - verticalPadding;
	} else {
		const view = gridElement.ownerDocument?.defaultView || window;
		const gridTop = gridElement.getBoundingClientRect().top;
		const bottomMargin = 32;
		availableHeight = view.innerHeight - gridTop - bottomMargin;
	}

	if (!Number.isFinite(availableHeight) || availableHeight < minimumHeight) {
		const rowCount = agGridRef.value?.api?.getDisplayedRowCount?.() || props.rowData.length || 0;
		availableHeight = Math.max(minimumHeight, Math.min(800, rowCount * 44 + 180));
	}

	gridElement.style.height = `${availableHeight}px`;
}

function autoSizeColumns(api) {
	const displayedColumns = api.getAllDisplayedColumns?.() || [];
	if (!displayedColumns.length) {
		return;
	}

	const displayedColumnIds = displayedColumns.map((column) => column.getColId()).filter(Boolean);

	api.autoSizeColumns({
		colKeys: displayedColumnIds,
		skipHeader: false,
		scaleUpToFitGridWidth: true,
	});
}

function onCellKeyDown(event) {
	if (event.event?.key === 'Enter') {
		event.api.stopEditing();
	}
}

function rowsChanged() {
	const api = agGridRef.value?.api;
	if (!api) {
		return;
	}

	const rowDataUpdated = [];
	api.forEachNodeAfterFilterAndSort((node, index) => {
		rowDataUpdated.push({ ...node.data, index: index + 1 });
	});
	emit('update:rowData', rowDataUpdated);

	nextTick(() => {
		onModelUpdated();
	});
}

function emitDisplayedRowCount() {
	const api = agGridRef.value?.api;
	if (!api) {
		emit('displayed-row-count-changed', Array.isArray(props.rowData) ? props.rowData.length : 0);
		return;
	}

	let displayedLeafRows = 0;
	api.forEachNodeAfterFilter((node) => {
		if (!node.group && node.data) {
			displayedLeafRows += 1;
		}
	});

	emit('displayed-row-count-changed', displayedLeafRows);
}

function emitRowGroupColumns() {
	const api = agGridRef.value?.api;
	if (!api || typeof api.getRowGroupColumns !== 'function') {
		emit('row-group-columns-changed', []);
		return;
	}

	const rowGroupColumns = (api.getRowGroupColumns() || []).map((column) => column?.getColId?.()).filter((colId) => typeof colId === 'string' && colId.length > 0);

	emit('row-group-columns-changed', rowGroupColumns);
}

function getGridApi() {
	return agGridRef.value?.api || null;
}

function safeClone(value) {
	if (value === null || value === undefined) {
		return value;
	}

	try {
		return JSON.parse(JSON.stringify(value));
	} catch {
		return value;
	}
}

function getViewState() {
	const api = getGridApi();
	if (!api) {
		return null;
	}

	const columnState = typeof api.getColumnState === 'function' ? api.getColumnState() : [];
	const filterModel = typeof api.getFilterModel === 'function' ? api.getFilterModel() : {};
	const rowGroupColumns = typeof api.getRowGroupColumns === 'function' ? (api.getRowGroupColumns() || []).map((column) => column?.getColId?.()).filter((colId) => typeof colId === 'string' && colId.length > 0) : [];

	return {
		columnState: safeClone(columnState),
		filterModel: safeClone(filterModel),
		rowGroupColumns: safeClone(rowGroupColumns),
	};
}

function removeRowGroupingFromColumnState(columnState = []) {
	if (!Array.isArray(columnState)) {
		return [];
	}

	return columnState.map((column) => {
		if (!column || typeof column !== 'object') {
			return column;
		}

		const normalizedColumn = { ...column };
		delete normalizedColumn.rowGroup;
		delete normalizedColumn.rowGroupIndex;

		return {
			...normalizedColumn,
		};
	});
}

function applyViewState(viewState = {}, options = {}) {
	const api = getGridApi();
	if (!api || !viewState || typeof viewState !== 'object') {
		return;
	}

	if (options?.internalReplay !== true) {
		lastAppliedViewState = safeClone(viewState);
		lastAppliedViewStateOptions = { ...options };
		remainingViewStateReplays = 3;
	}

	const preserveRowGroups = options?.preserveRowGroups === true;
	const rawColumnState = Array.isArray(viewState.columnState) ? (preserveRowGroups ? removeRowGroupingFromColumnState(viewState.columnState) : viewState.columnState) : [];
	if (rawColumnState.length && !hasAvailableColumns()) {
		pendingViewState = safeClone(viewState);
		pendingViewStateOptions = { ...options };
		return;
	}

	pendingViewState = null;
	pendingViewStateOptions = null;
	const columnState = filterColumnStateToAvailableColumns(rawColumnState);

	if (columnState.length && typeof api.applyColumnState === 'function') {
		api.applyColumnState({
			state: columnState,
			applyOrder: true,
		});
	}

	if (!preserveRowGroups && Array.isArray(viewState.rowGroupColumns)) {
		setRowGroupColumns(viewState.rowGroupColumns);
	}

	const filterModel = normalizeFilterModelForRowGroups(viewState.filterModel, viewState.rowGroupColumns);
	applyFilterModel(filterModel);

	api.refreshClientSideRowModel('filter');
	syncGridLayout();
	emitDisplayedRowCount();
	emitRowGroupColumns();
}

function replayLastAppliedViewStateIfNeeded() {
	if (!lastAppliedViewState || remainingViewStateReplays <= 0 || viewStateReplayFrameId !== null) {
		return;
	}

	viewStateReplayFrameId = requestAnimationFrame(() => {
		viewStateReplayFrameId = null;
		if (!lastAppliedViewState || remainingViewStateReplays <= 0) {
			return;
		}

		remainingViewStateReplays -= 1;
		applyViewState(lastAppliedViewState, {
			...(lastAppliedViewStateOptions || {}),
			internalReplay: true,
		});
	});
}

function applyPendingViewStateIfReady() {
	if (!pendingViewState) {
		return;
	}

	if (Array.isArray(pendingViewState.columnState) && pendingViewState.columnState.length && !hasAvailableColumns()) {
		return;
	}

	const nextViewState = pendingViewState;
	const nextOptions = pendingViewStateOptions || {};
	pendingViewState = null;
	pendingViewStateOptions = null;
	applyViewState(nextViewState, nextOptions);
}

function hasAvailableColumns() {
	const api = getGridApi();
	return (api?.getColumns?.() || []).length > 0;
}

function filterColumnStateToAvailableColumns(columnState = []) {
	const availableColIds = new Set((getGridApi()?.getColumns?.() || []).map((column) => column?.getColId?.()).filter(Boolean));
	if (!availableColIds.size) {
		return [];
	}

	return (Array.isArray(columnState) ? columnState : []).filter((column) => availableColIds.has(column?.colId));
}

function normalizeFilterModelForRowGroups(filterModel, rowGroupColumns = []) {
	if (!filterModel || typeof filterModel !== 'object') {
		return null;
	}

	const normalizedFilterModel = safeClone(filterModel) || {};
	const groupFilter = getSavedGroupFilter(normalizedFilterModel, rowGroupColumns);
	if (!groupFilter) {
		return filterFilterModelToAvailableColumns(normalizedFilterModel);
	}

	const currentRowGroupColumns =
		typeof getGridApi()?.getRowGroupColumns === 'function'
			? getGridApi()
					.getRowGroupColumns()
					.map((column) => column?.getColId?.())
					.filter(Boolean)
			: [];
	const groupColumnIds = [...new Set([...(Array.isArray(rowGroupColumns) ? rowGroupColumns : []), ...currentRowGroupColumns].filter(Boolean))];

	groupColumnIds.forEach((colId) => {
		normalizedFilterModel[colId] = safeClone(groupFilter);
	});

	return filterFilterModelToAvailableColumns(normalizedFilterModel);
}

function getSavedGroupFilter(filterModel = {}, rowGroupColumns = []) {
	const filterEntries = Object.entries(filterModel);
	const currentRowGroupColumns =
		typeof getGridApi()?.getRowGroupColumns === 'function'
			? getGridApi()
					.getRowGroupColumns()
					.map((column) => column?.getColId?.())
					.filter(Boolean)
			: [];
	const rowGroupColumnSet = new Set([...(Array.isArray(rowGroupColumns) ? rowGroupColumns : []), ...currentRowGroupColumns].filter(Boolean));

	for (const [colId, model] of filterEntries) {
		if (colId === AUTO_GROUP_COLUMN_ID || colId.startsWith(`${AUTO_GROUP_COLUMN_ID}-`) || rowGroupColumnSet.has(colId)) {
			return model;
		}
	}

	return null;
}

function filterFilterModelToAvailableColumns(filterModel = {}) {
	const availableColIds = new Set((getGridApi()?.getColumns?.() || []).map((column) => column?.getColId?.()).filter(Boolean));
	if (!availableColIds.size) {
		return filterModel;
	}

	return Object.entries(filterModel).reduce((normalizedModel, [colId, model]) => {
		if (availableColIds.has(colId)) {
			normalizedModel[colId] = model;
		}
		return normalizedModel;
	}, {});
}

function applyFilterModel(filterModel) {
	const api = getGridApi();
	if (!api || !filterModel || typeof filterModel !== 'object' || Object.keys(filterModel).length === 0) {
		return;
	}

	const availableFilterModel = filterFilterModelToAvailableColumns(filterModel);
	if (typeof api.setFilterModel === 'function') {
		api.setFilterModel(availableFilterModel);
	}

	if (typeof api.setColumnFilterModel === 'function') {
		Promise.allSettled(Object.entries(availableFilterModel).map(([colId, model]) => api.setColumnFilterModel(colId, model))).then(() => {
			const latestApi = getGridApi();
			latestApi?.onFilterChanged?.();
			latestApi?.refreshClientSideRowModel?.('filter');
			emitDisplayedRowCount();
			emitRowGroupColumns();
		});
	}
}

function setRowGroupColumns(colIds = []) {
	const api = getGridApi();
	if (!api) {
		return;
	}

	const rowGroupColIds = [...new Set((Array.isArray(colIds) ? colIds : []).filter((colId) => typeof colId === 'string' && colId.length > 0))];
	const currentRowGroupColIds =
		typeof api.getRowGroupColumns === 'function'
			? api
					.getRowGroupColumns()
					.map((column) => column?.getColId?.())
					.filter(Boolean)
			: [];
	if (haveSameItems(rowGroupColIds, currentRowGroupColIds)) {
		emitDisplayedRowCount();
		emitRowGroupColumns();
		return;
	}

	api.applyColumnState({
		defaultState: {
			rowGroup: false,
			rowGroupIndex: null,
		},
		state: rowGroupColIds.map((colId, rowGroupIndex) => ({
			colId,
			rowGroup: true,
			rowGroupIndex,
		})),
	});
	api.refreshClientSideRowModel('group');
	syncGridLayoutWithColumnFit();
	emitDisplayedRowCount();
	emitRowGroupColumns();
}

function haveSameItems(first = [], second = []) {
	if (first.length !== second.length) {
		return false;
	}

	const secondSet = new Set(second);
	return first.every((item) => secondSet.has(item));
}

function exportCsv(params = {}) {
	const api = getGridApi();
	if (api && typeof api.exportDataAsCsv === 'function') {
		api.exportDataAsCsv(params);
	}
}

function generateChart(options = {}) {
	const api = getGridApi();
	if (!api || typeof api.createRangeChart !== 'function') {
		return null;
	}

	const displayedColumns = api.getAllDisplayedColumns?.() || [];
	const colIds = displayedColumns.map((column) => column.getColId()).filter(Boolean);
	const categoryColId = options.category || colIds[0];
	const valueColIds = Array.isArray(options.columns) && options.columns.length ? options.columns : colIds.filter((colId) => colId !== categoryColId);

	if (!valueColIds.length) {
		return null;
	}

	try {
		return api.createRangeChart({
			chartType: options.chartType || 'groupedColumn',
			cellRange: {
				columns: [categoryColId, ...valueColIds],
			},
			aggFunc: options.aggFunc,
		});
	} catch (error) {
		return null;
	}
}

defineExpose({
	getGridApi,
	getViewState,
	applyViewState,
	setRowGroupColumns,
	exportCsv,
	generateChart,
});
</script>
